// 使用 TinyMCE Cloud 版本，不需要本地导入
// TinyMCE Cloud 脚本在布局文件中通过 CDN 加载

import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
    static targets = ['input']

    initialize() {
        this.defaults = {
            language: 'zh-CN',

            plugins: [
                'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                'anchor', 'searchreplace', 'visualblocks', 'advcode', 'fullscreen',
                'insertdatetime', 'media', 'table', 'autoresize', 'wordcount', 'powerpaste', 'advcode', 'codesample'
            ],
            toolbar: 'undo redo | insert | styles | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image advcode codesample',
            toolbar_sticky: true,
            file_picker_types: 'file image media',
            relative_urls: false,
            // 自定义 URL 转换器，防止对以 {{ 开头的变量链接添加 /backend/ 前缀
            urlconverter_callback: this.urlConverter.bind(this),
            // 图片上传配置 - 使用自定义处理器处理CSRF
            images_upload_handler: this.uploadImageHandler.bind(this),
            skin: 'oxide-dark',
            // 设置深色内容样式（编辑区域）
            content_css: 'dark',
            // 自定义内容样式，确保背景色为深色
            content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:16px; background-color: #1f2937; color: #f9fafb; }',
            // 移除不必要的菜单项
            contextmenu: 'link image table',
            menubar: true,
            // PowerPaste 配置 - 保留样式
            powerpaste_allow_local_images: true,
            powerpaste_word_import: 'merge', // 使用 merge 模式保留样式
            powerpaste_html_import: 'merge', // 使用 merge 模式保留样式
            powerpaste_keep_unsupported_src: true, // 保留不支持的图片源

            // Paste 配置 - 保留样式和格式，但移除颜色和背景色以保持风格统一
            paste_as_text: false, // 不转换为纯文本
            paste_retain_style_properties: 'font-size font-family font-weight font-style text-align text-decoration line-height margin padding border', // 保留样式属性，但不包括颜色
            paste_remove_styles_if_webkit: false, // 不删除 WebKit 样式
            paste_remove_spans: false, // 不删除 span 标签
            paste_strip_class_attributes: 'none', // 不删除 class 属性

            // 清理颜色和背景色的辅助函数
            setup: (editor) => {
                // 粘贴后清理颜色
                editor.on('PastePostProcess', (e) => {
                    const content = e.node
                    if (content) {
                        const allElements = content.querySelectorAll('*')
                        allElements.forEach((el) => {
                            if (el.style) {
                                el.style.removeProperty('color')
                                el.style.removeProperty('background-color')
                                el.style.removeProperty('background')
                                if (!el.style.cssText || el.style.cssText.trim() === '') {
                                    el.removeAttribute('style')
                                }
                            }
                            // 移除 font 标签的 color 属性
                            if (el.tagName === 'FONT' && el.hasAttribute('color')) {
                                el.removeAttribute('color')
                            }
                        })
                    }
                })
            },

            // 允许更多 HTML 元素和属性（不包含 color 属性以保持风格统一）
            extended_valid_elements: 'a[href|target|rel|data-popover|style|class],' +
                'p[style|class|align],' +
                'div[style|class|align],' +
                'span[style|class],' +
                'table[style|class|width|border|cellpadding|cellspacing|align],' +
                'tr[style|class|align|valign],' +
                'td[style|class|align|valign|colspan|rowspan|width|height],' +
                'th[style|class|align|valign|colspan|rowspan|width|height],' +
                'ul[style|class],' +
                'ol[style|class],' +
                'li[style|class],' +
                'h1[style|class|align],' +
                'h2[style|class|align],' +
                'h3[style|class|align],' +
                'h4[style|class|align],' +
                'h5[style|class|align],' +
                'h6[style|class|align],' +
                'img[src|alt|title|width|height|style|class|align],' +
                'strong[style|class],' +
                'em[style|class],' +
                'u[style|class],' +
                's[style|class],' +
                'font[size|face|style],' + // 移除 color 属性
                'br[style|class]',
            // ⭐️ 配置 codesample 路径 (重要！用于加载 Prism.js 主题和语言)
            // 这里的路径需要指向您的资产打包系统能找到的位置
            codesample_global_prismjs: true, // 推荐：使用全局的Prism.js实例 (如果您的项目有全局引入)
            codesample_languages: [ // 可选：指定您需要的语言
                { text: 'HTML/XML', value: 'markup' },
                { text: 'JavaScript', value: 'javascript' },
                { text: 'CSS', value: 'css' },
                { text: 'Ruby', value: 'ruby' },
                { text: 'Rails', value: 'ruby' },
                { text: 'Bash/Shell', value: 'bash' }
            ]
        }
    }

    connect() {
        // Initialize the app
        let config = Object.assign({ target: this.inputTarget }, this.defaults)
        const editor = tinymce.init(config);

        // 确保表单提交前同步编辑器内容到 textarea，并清理颜色样式
        const form = this.inputTarget.closest('form')
        if (form) {
            form.addEventListener('submit', (e) => {
                const editorInstance = tinymce.get(this.inputTarget.id)
                if (editorInstance) {
                    // 获取编辑器内容
                    let content = editorInstance.getContent()

                    // 创建临时 DOM 来清理颜色和背景色
                    const tempDiv = document.createElement('div')
                    tempDiv.innerHTML = content

                    // 移除所有元素的颜色和背景色样式
                    const allElements = tempDiv.querySelectorAll('*')
                    allElements.forEach((el) => {
                        // 移除内联样式中的颜色
                        if (el.style) {
                            el.style.removeProperty('color')
                            el.style.removeProperty('background-color')
                            el.style.removeProperty('background')
                            if (!el.style.cssText || el.style.cssText.trim() === '') {
                                el.removeAttribute('style')
                            }
                        }
                        // 移除 font 标签的 color 属性
                        if (el.tagName === 'FONT' && el.hasAttribute('color')) {
                            el.removeAttribute('color')
                        }
                    })

                    // 更新编辑器内容
                    editorInstance.setContent(tempDiv.innerHTML)
                    // 同步到 textarea
                    editorInstance.save()
                }
            })
        }
    }

    disconnect() {
        tinymce.remove()
    }

    // URL 转换器 - 如果链接以 {{ 开头（模型变量），不添加 /backend/ 前缀
    urlConverter(url, node, on_save) {
        // 如果 URL 以 {{ 开头（模型变量占位符），保持原样，不转换
        // 格式：{{ModelName:identifier:method}} 或 {{ModelName:method}}
        if (url && url.trim().startsWith('{{')) {
            return url
        }

        // 对于其他 URL，使用默认行为（不转换）
        return url
    }

    // 图片上传处理器 - 使用fetch
    uploadImageHandler(blobInfo, progress) {
        const formData = new FormData()
        formData.append('file', blobInfo.blob(), blobInfo.filename())

        // 获取CSRF令牌
        const csrfToken = document.querySelector('meta[name="csrf-token"]')
        if (!csrfToken) {
            return Promise.reject(new Error('CSRF令牌未找到'))
        }

        return fetch('/backend/image_uploads', {
            method: 'POST',
            body: formData,
            headers: {
                'X-CSRF-Token': csrfToken.getAttribute('content')
            }
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`上传失败: HTTP ${response.status}`)
                }
                return response.json()
            })
            .then(result => {
                if (result.location) {
                    return result.location
                } else {
                    throw new Error(result.error || '上传失败')
                }
            })
    }

}
